import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';
import { queryKeys } from '@/lib/queryKeys';
import type { AssignmentRole, OrderType, PersonnelOrder } from '@/lib/types';

/**
 * 인사발령 — 전술판에서 옮긴 결과를 <b>한 장으로</b> 확정하고, 그 이력을 읽는다.
 *
 * <h2>왜 카드를 옮길 때마다 저장하지 않는가</h2>
 * "A를 현장으로 보내고 그 자리에 B를 넣는다" 는 사람에게 <b>한 번의 인사발령</b>이다. 옮길 때마다
 * 저장하면 두 사건이 되어 중간 상태(자리가 빈 채)가 남에게 보이고, 되돌리려면 두 번 되돌려야 한다.
 * 그래서 옮긴 결과는 브라우저에만 쌓이고, 「발령 확정」 한 번이 서버로 간다.
 *
 * <h2>편집 가능 여부는 서버가 답한다</h2>
 * 대표·본부장급 또는 인사배치 담당만 카드를 옮긴다(`PlacementAuthority`). 그 판정은 직급 순위와
 * 역할을 함께 보는 것이라 브라우저가 흉내 낼 수 없다 — 세션의 `menuPermissions` 에는 그 층이 없다.
 * 그래서 {@link usePlacementAuthority} 가 서버에 한 번 묻고, 화면은 그 답으로 버튼을 띄운다.
 * (물론 표시용이다. 실제 판정은 확정 요청에서 서버가 다시 한다.)
 */

/** 전술판이 확정 시 서버로 보내는 변경 한 줄. 그대로인 카드는 실리지 않는다. */
export type PlacementChange = {
  personnelId: string;
  orderType: OrderType;
  assignmentType: 'department' | 'site';
  /** 옮기거나 푸는 대상 배치. 새로 얹는 겸직·파견이면 null. */
  assignmentId: string | null;
  /** 도착 부서·현장. 해제면 null. */
  toName: string | null;
  roleTitle: string | null;
  fieldName: string | null;
  teamLead: boolean;
  endDate: string | null;
  /** null 이면 서버 기본 규칙(파견·현장 발령 = 대상)을 따른다. */
  allowanceEligible: boolean | null;
  allowanceNote: string | null;
  /** 겸직 실근무 확인 두 칸(V590). 둘 다 체크돼야 겸직수당 대상이다. */
  workCheckStrategy?: boolean;
  workCheckSiteAgent?: boolean;
};

export type PlacementOrderForm = {
  effectiveDate: string;
  reason: string | null;
  changes: PlacementChange[];
};

/**
 * 두 가지를 함께 묻는다 — <b>전술판을 고칠 수 있는가</b>와 <b>명부를 인쇄할 수 있는가</b>.
 * 축이 다르다(서버 `PlacementAuthority`): 배치 변경은 직위·지목이, 인쇄는 소속이 정한다.
 * 인쇄는 브라우저가 하는 일이라 서버가 막을 요청이 없어, 이 답이 곧 버튼의 유무다.
 */
export function usePlacementAuthority() {
  return useQuery({
    queryKey: queryKeys.placementAuthority,
    queryFn: () => apiFetch<{ canEdit: boolean; canPrint: boolean }>('/v1/placement-orders/authority'),
    staleTime: 5 * 60_000,
  });
}

/**
 * 발령 이력.
 *
 * @param personnelId 비우면 전체(배치표의 「변경 이력」). 사람을 주면 그 사람 것만.
 * @param limit       0 이면 전부.
 */
export function usePlacementOrders(personnelId?: string | null, limit = 0, status?: PersonnelOrder['status']) {
  return useQuery({
    queryKey: [...queryKeys.placementOrders, personnelId ?? 'all', limit, status ?? 'any'],
    queryFn: () => {
      const params = new URLSearchParams();
      if (personnelId) params.set('personnelId', personnelId);
      if (status) params.set('status', status);
      if (limit > 0) params.set('limit', String(limit));
      const query = params.toString();
      return apiFetch<PersonnelOrder[]>(`/v1/placement-orders${query ? `?${query}` : ''}`);
    },
  });
}

/**
 * 아직 오지 않은 발령 — 전술판이 판 위에 <b>점선 카드</b>로 세운다(V247 · 사용자 지시 2026-09-09).
 *
 * <p>확정한 사람에게 발령일까지 아무 흔적도 안 보이면 「저장이 안 됐나」 가 되고, 그날 아침에는
 * 카드가 갑자기 움직여 있다. 예정이 판 위에 서 있으면 둘 다 없다.
 */
export function useScheduledOrders() {
  return usePlacementOrders(null, 0, 'SCHEDULED');
}

/**
 * 확정·취소. 끝나면 배치·발령·내 프로필을 함께 다시 읽는다 —
 * 발령 하나가 배치표·개인 대시보드·직원찾기 명함을 동시에 바꾸기 때문이다.
 */
export function usePlacementOrderWrite() {
  const queryClient = useQueryClient();
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.personnelAssignments });
    queryClient.invalidateQueries({ queryKey: queryKeys.placementOrders });
    queryClient.invalidateQueries({ queryKey: queryKeys.personnel });
    queryClient.invalidateQueries({ queryKey: queryKeys.homeProfile });
    queryClient.invalidateQueries({ queryKey: ['org-layout'] });
  };

  const confirm = useMutation({
    mutationFn: (form: PlacementOrderForm) =>
      apiFetch<PersonnelOrder[]>('/v1/placement-orders', { method: 'POST', body: form }),
    onSuccess: invalidate,
  });

  const cancel = useMutation({
    mutationFn: (orderId: string) =>
      apiFetch<PersonnelOrder>(`/v1/placement-orders/${orderId}/cancel`, { method: 'PUT' }),
    onSuccess: invalidate,
  });

  return { confirm, cancel };
}

/** 발령 종류가 남기는 배치 성격. 전술판이 카드에 뱃지를 그릴 때와 같은 표다(서버 `OrderType.assignmentRole`). */
export function roleOfOrderType(type: OrderType): AssignmentRole | null {
  switch (type) {
    case 'TRANSFER':
      return 'primary';
    case 'CONCURRENT':
      return 'concurrent';
    case 'DISPATCH':
      return 'dispatch';
    case 'RELEASE':
      return null;
  }
}

/**
 * 수당 대상 기본값 — 서버 `PlacementOrderService.defaultAllowanceEligible` 과 <b>같은 규칙</b>이다.
 * 화면이 확정 창에 미리 체크를 켜 두기 위해 한 번 계산하고, 사람이 끄면 그 값이 그대로 서버로 간다.
 */
export function defaultAllowanceEligible(type: OrderType, assignmentType: 'department' | 'site'): boolean {
  if (type === 'DISPATCH') return true;
  if (type === 'RELEASE') return false;
  return assignmentType === 'site';
}
